import React, { useState, useRef, useEffect, useCallback } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { X, Loader2, UserCircle, ChevronDown, AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails, Module } from '../index'
import type { TargetItem } from './TargetsTab'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4050'

// A B2C organisation has no subscriptions: its modules are attached directly, and an
// active `b2c_modules` row is what backs an enrolment there, exactly as an unexpired
// subscription does for B2B. Without this the module list came out empty and no target
// could be created, which left B2C users on a main screen with no roleplay cards.
const GET_B2C_MODULES = gql`
  query getB2cModules($organisationId: Int!) {
    getB2cModules(organisationId: $organisationId) {
      moduleId
      isActive
    }
  }
`

interface CreateTargetModalProps {
  isOpen: boolean
  onClose: () => void
  organisation: OrganisationWithDetails
  modules: Module[]
  onSuccess: () => void
  editTarget?: TargetItem | null
}

interface FoldOption {
  id: number
  name: string
  description: string | null
}

interface FoldPayload {
  id: number
  name: string
  description: string | null
}

interface FoldPayloadsResult {
  payloads: FoldPayload[]
  foldName: string | null
}

interface CoachPayload {
  id: number
  name: string
  description: string | null
}

interface AvatarOption {
  id: number
  name: string
  description: string | null
}

const GET_FOLDS = gql`
  query getFolds($projectId: Int!) {
    getFolds(projectId: $projectId) {
      id
      name
      description
    }
  }
`

const GET_PAYLOADS = gql`
  query getPayloads($projectId: Int!, $limit: Int) {
    getPayloads(projectId: $projectId, limit: $limit) {
      data {
        id
        name
        description
      }
    }
  }
`

const GET_FOLD_PAYLOADS = gql`
  query getFoldPayloads($foldId: Int!) {
    getFoldPayloads(foldId: $foldId) {
      payloads {
        id
        name
        description
      }
      foldName
    }
  }
`

const GET_AVATARS = gql`
  query GetAvatars($search: String, $limit: Int, $offset: Int) {
    getAvatars(search: $search, limit: $limit, offset: $offset) {
      id
      name
      description
    }
  }
`

const CREATE_TARGET = gql`
  mutation createTarget($input: CreateTargetInput!) {
    createTarget(input: $input) {
      id
      moduleId
      organisationId
      payloadId
      payloadName
      coachPayloadId
      elevenlabsVoiceId
      avatarId
      minRating
      minSessions
    }
  }
`

const UPDATE_TARGET = gql`
  mutation updateTarget($targetId: Int!, $input: CreateTargetInput!) {
    updateTarget(targetId: $targetId, input: $input) {
      id
      moduleId
      organisationId
      payloadId
      payloadName
      coachPayloadId
      elevenlabsVoiceId
      avatarId
      minRating
      minSessions
    }
  }
`

const CreateTargetModal: React.FC<CreateTargetModalProps> = ({ isOpen, onClose, organisation, modules, onSuccess, editTarget }) => {
  const modalRef = useRef<HTMLDivElement>(null)

  // Module selection
  const [selectedModuleId, setSelectedModuleId] = useState<number | ''>('')

  // Folds state
  const [folds, setFolds] = useState<FoldOption[]>([])
  const [loadingFolds, setLoadingFolds] = useState(false)
  const [foldsError, setFoldsError] = useState<string | null>(null)
  const [selectedFoldId, setSelectedFoldId] = useState<number | ''>('')

  // Roleplay payload state (loaded from selected fold)
  const [payloads, setPayloads] = useState<FoldPayload[]>([])
  const [foldName, setFoldName] = useState<string | null>(null)
  const [selectedPayloadId, setSelectedPayloadId] = useState<number | ''>('')
  const [selectedPayloadName, setSelectedPayloadName] = useState('')
  const [loadingPayloads, setLoadingPayloads] = useState(false)

  // Coach payload state
  const [coachPayloads, setCoachPayloads] = useState<CoachPayload[]>([])
  const [loadingCoachPayloads, setLoadingCoachPayloads] = useState(false)
  const [coachPayloadsError, setCoachPayloadsError] = useState<string | null>(null)
  const [selectedCoachPayloadId, setSelectedCoachPayloadId] = useState<number | null>(null)
  // Fallback for edit mode when coachPayloadId isn't in the loaded list
  const [coachPayloadFallback, setCoachPayloadFallback] = useState<{ id: number; name: string } | null>(null)

  // Avatar state
  const [avatarId, setAvatarId] = useState<number | null>(null)
  const [avatarSearch, setAvatarSearch] = useState('')
  const [avatarOptions, setAvatarOptions] = useState<AvatarOption[]>([])
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false)
  const [loadingAvatars, setLoadingAvatars] = useState(false)
  const [selectedAvatarName, setSelectedAvatarName] = useState('')
  const avatarSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const avatarDropdownRef = useRef<HTMLDivElement>(null)

  // Other fields
  const [minRating, setMinRating] = useState('0')
  const [minSessions, setMinSessions] = useState('0')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditMode = !!editTarget
  const isB2c = organisation.kind === 'b2c'

  // Which modules this organisation may have targets for: its active B2C modules, or
  // the modules it has an unexpired subscription to.
  const [b2cModuleIds, setB2cModuleIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!isOpen || !isB2c) return
    let cancelled = false
    authorizedGraphQLRequest<{ getB2cModules: { moduleId: number; isActive: boolean }[] }>(GET_B2C_MODULES, {
      organisationId: organisation.id,
    })
      .then((response) => {
        if (cancelled) return
        setB2cModuleIds(new Set((response.getB2cModules || []).filter((m) => m.isActive).map((m) => m.moduleId)))
      })
      .catch((err) => console.error('Failed to load B2C modules:', err))
    return () => {
      cancelled = true
    }
  }, [isOpen, isB2c, organisation.id])

  const subscribedModuleIds = new Set((organisation.activeSubscriptions || []).map((s) => s.moduleId))
  const backedModuleIds = isB2c ? b2cModuleIds : subscribedModuleIds
  const availableModules = modules.filter((m) => backedModuleIds.has(m.id))

  // ── Microfrank data fetchers ──────────────────────────────────────────────

  const fetchFoldsAndPayloads = useCallback(async (projectId: number) => {
    setLoadingFolds(true)
    setLoadingCoachPayloads(true)
    setFoldsError(null)
    setCoachPayloadsError(null)
    setFolds([])
    setCoachPayloads([])
    setSelectedFoldId('')
    setPayloads([])
    setFoldName(null)
    setSelectedPayloadId('')
    setSelectedPayloadName('')
    setSelectedCoachPayloadId(null)
    setCoachPayloadFallback(null)

    const [foldsResult, payloadsResult] = await Promise.allSettled([
      authorizedGraphQLRequest<{ getFolds: FoldOption[] }>(GET_FOLDS, { projectId }),
      authorizedGraphQLRequest<{ getPayloads: { data: CoachPayload[] } }>(GET_PAYLOADS, { projectId, limit: 100 }),
    ])

    if (foldsResult.status === 'fulfilled') {
      setFolds(foldsResult.value.getFolds || [])
    } else {
      setFoldsError('Failed to load folds')
    }
    setLoadingFolds(false)

    if (payloadsResult.status === 'fulfilled') {
      setCoachPayloads(payloadsResult.value.getPayloads?.data || [])
    } else {
      setCoachPayloadsError('Failed to load payloads')
    }
    setLoadingCoachPayloads(false)
  }, [])

  const fetchFoldPayloads = useCallback(async (foldId: number) => {
    try {
      setLoadingPayloads(true)
      setPayloads([])
      setFoldName(null)
      setSelectedPayloadId('')
      setSelectedPayloadName('')
      const response = await authorizedGraphQLRequest<{ getFoldPayloads: FoldPayloadsResult }>(GET_FOLD_PAYLOADS, { foldId })
      setPayloads(response.getFoldPayloads.payloads || [])
      setFoldName(response.getFoldPayloads.foldName)
    } catch (err: any) {
      console.error('Failed to fetch fold payloads:', err)
    } finally {
      setLoadingPayloads(false)
    }
  }, [])

  // ── Module change handler ─────────────────────────────────────────────────

  const handleModuleChange = useCallback(
    (moduleId: number | '') => {
      setSelectedModuleId(moduleId)
      setFolds([])
      setFoldsError(null)
      setSelectedFoldId('')
      setPayloads([])
      setFoldName(null)
      setSelectedPayloadId('')
      setSelectedPayloadName('')
      setCoachPayloads([])
      setCoachPayloadsError(null)
      setSelectedCoachPayloadId(null)
      setCoachPayloadFallback(null)

      if (!moduleId) return
      const module = modules.find((m) => m.id === moduleId)
      if (module?.microfankProjectId) {
        fetchFoldsAndPayloads(module.microfankProjectId)
      }
    },
    [modules, fetchFoldsAndPayloads],
  )

  // ── Avatar helpers ────────────────────────────────────────────────────────

  const fetchAvatars = useCallback(async (search: string) => {
    try {
      setLoadingAvatars(true)
      const data = await authorizedGraphQLRequest<{ getAvatars: AvatarOption[] }>(GET_AVATARS, { search: search || null, limit: 20, offset: 0 })
      setAvatarOptions(data.getAvatars || [])
    } catch (err) {
      console.error('Failed to fetch avatars:', err)
    } finally {
      setLoadingAvatars(false)
    }
  }, [])

  useEffect(() => {
    if (!avatarDropdownOpen) return
    if (avatarSearchTimeout.current) clearTimeout(avatarSearchTimeout.current)
    avatarSearchTimeout.current = setTimeout(() => fetchAvatars(avatarSearch), 250)
    return () => {
      if (avatarSearchTimeout.current) clearTimeout(avatarSearchTimeout.current)
    }
  }, [avatarSearch, avatarDropdownOpen, fetchAvatars])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(e.target as Node)) {
        setAvatarDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAvatarSelect = (avatar: AvatarOption | null) => {
    setAvatarId(avatar?.id ?? null)
    setSelectedAvatarName(avatar?.name ?? '')
    setAvatarDropdownOpen(false)
    setAvatarSearch('')
  }

  // ── Edit mode population ──────────────────────────────────────────────────

  useEffect(() => {
    if (!editTarget || !isOpen) return

    setSelectedModuleId(editTarget.moduleId)
    setSelectedPayloadId(editTarget.payloadId)
    setSelectedPayloadName(editTarget.payloadName)
    setAvatarId(editTarget.avatarId ?? null)
    setMinRating(String(editTarget.minRating))
    setMinSessions(String(editTarget.minSessions))

    if (editTarget.avatarId) {
      authorizedGraphQLRequest<{ getAvatars: AvatarOption[] }>(GET_AVATARS, { limit: 100, offset: 0 })
        .then((data) => {
          const found = (data.getAvatars || []).find((a) => a.id === editTarget.avatarId)
          if (found) setSelectedAvatarName(found.name)
        })
        .catch(() => {})
    }

    const module = modules.find((m) => m.id === editTarget.moduleId)
    if (module?.microfankProjectId) {
      // Load folds + payloads, then pre-select coach payload
      setLoadingFolds(true)
      setLoadingCoachPayloads(true)
      Promise.allSettled([
        authorizedGraphQLRequest<{ getFolds: FoldOption[] }>(GET_FOLDS, { projectId: module.microfankProjectId }),
        authorizedGraphQLRequest<{ getPayloads: { data: CoachPayload[] } }>(GET_PAYLOADS, { projectId: module.microfankProjectId, limit: 100 }),
      ]).then(([foldsResult, payloadsResult]) => {
        if (foldsResult.status === 'fulfilled') {
          setFolds(foldsResult.value.getFolds || [])
        } else {
          setFoldsError('Failed to load folds')
        }
        setLoadingFolds(false)

        if (payloadsResult.status === 'fulfilled') {
          const list = payloadsResult.value.getPayloads?.data || []
          setCoachPayloads(list)

          if (editTarget.coachPayloadId != null) {
            const found = list.find((p) => p.id === editTarget.coachPayloadId)
            if (found) {
              setSelectedCoachPayloadId(found.id)
            } else {
              setCoachPayloadFallback({ id: editTarget.coachPayloadId, name: `(ID: ${editTarget.coachPayloadId} — not found)` })
              setSelectedCoachPayloadId(editTarget.coachPayloadId)
            }
          }
        } else {
          setCoachPayloadsError('Failed to load payloads')
          if (editTarget.coachPayloadId != null) {
            setCoachPayloadFallback({ id: editTarget.coachPayloadId, name: `(ID: ${editTarget.coachPayloadId} — not found)` })
            setSelectedCoachPayloadId(editTarget.coachPayloadId)
          }
        }
        setLoadingCoachPayloads(false)
      })
    }
  }, [editTarget, isOpen, modules])

  // ── Keyboard / close ──────────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const handleClose = () => {
    setSelectedModuleId('')
    setFolds([])
    setLoadingFolds(false)
    setFoldsError(null)
    setSelectedFoldId('')
    setPayloads([])
    setFoldName(null)
    setSelectedPayloadId('')
    setSelectedPayloadName('')
    setLoadingPayloads(false)
    setCoachPayloads([])
    setLoadingCoachPayloads(false)
    setCoachPayloadsError(null)
    setSelectedCoachPayloadId(null)
    setCoachPayloadFallback(null)
    setAvatarId(null)
    setSelectedAvatarName('')
    setAvatarSearch('')
    setAvatarDropdownOpen(false)
    setMinRating('0')
    setMinSessions('0')
    setError(null)
    onClose()
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedModuleId) {
      setError('Please select a module')
      return
    }
    if (!selectedPayloadId || !selectedPayloadName) {
      setError('Please select a fold and then select a payload')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const input = {
        moduleId: selectedModuleId,
        organisationId: organisation.id,
        payloadId: selectedPayloadId,
        payloadName: selectedPayloadName,
        coachPayloadId: selectedCoachPayloadId ?? null,
        avatarId: avatarId ?? null,
        minRating: parseInt(minRating, 10) || 0,
        minSessions: parseInt(minSessions, 10) || 0,
      }

      if (isEditMode && editTarget) {
        await authorizedGraphQLRequest(UPDATE_TARGET, { targetId: editTarget.id, input })
      } else {
        await authorizedGraphQLRequest(CREATE_TARGET, { input })
      }

      onSuccess()
    } catch (err: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} target:`, err)
      setError(err?.response?.errors?.[0]?.message || `Failed to ${isEditMode ? 'update' : 'create'} target`)
    } finally {
      setSubmitting(false)
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const selectedModule = modules.find((m) => m.id === selectedModuleId)
  const hasProject = !!selectedModule?.microfankProjectId
  const noProjectWarning = selectedModuleId !== '' && !hasProject

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div className="fixed inset-0 bg-black/40" />

      <div ref={modalRef} className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-150">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">{isEditMode ? 'Edit Target' : 'Create Target'}</h2>
          <button onClick={handleClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          {/* Module Selection — the modules this organisation actually backs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Module <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedModuleId}
              onChange={(e) => handleModuleChange(e.target.value ? parseInt(e.target.value, 10) : '')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value="">Select a module</option>
              {availableModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </select>
            {availableModules.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">{isB2c ? 'No active modules on this organisation. Add one in the Modules tab.' : 'No active subscriptions found for this organisation'}</p>
            )}
          </div>

          {/* No microfankProjectId warning */}
          {noProjectWarning && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">This module has no Microfrank project configured. Edit the module to add a project ID before creating targets.</p>
            </div>
          )}

          {/* Fold Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fold <span className="text-red-500">*</span>
            </label>

            {/* Edit mode: show current payload as read-only */}
            {isEditMode && selectedPayloadName && !selectedFoldId && (
              <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-700">
                  <strong>Current payload:</strong> {selectedPayloadName}
                </p>
                <p className="text-xs text-green-600 mt-0.5">Select a fold below to replace it</p>
              </div>
            )}

            {loadingFolds ? (
              <div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading folds…
              </div>
            ) : foldsError ? (
              <div className="flex items-center gap-2 px-4 py-2 border border-red-200 bg-red-50 rounded-lg text-sm text-red-700">
                <span className="flex-1">{foldsError}</span>
                <button type="button" onClick={() => selectedModule?.microfankProjectId && fetchFoldsAndPayloads(selectedModule.microfankProjectId)} className="shrink-0">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <select
                value={selectedFoldId}
                onChange={(e) => {
                  const foldId = e.target.value ? parseInt(e.target.value, 10) : ''
                  setSelectedFoldId(foldId)
                  if (foldId) fetchFoldPayloads(foldId)
                  else {
                    setPayloads([])
                    setFoldName(null)
                    setSelectedPayloadId('')
                    setSelectedPayloadName('')
                  }
                }}
                disabled={!hasProject || folds.length === 0}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">{!selectedModuleId ? 'Select a module first' : folds.length === 0 ? 'No folds available' : 'Select a fold'}</option>
                {folds.map((fold) => (
                  <option key={fold.id} value={fold.id}>
                    {fold.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Fold payloads */}
          {foldName && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Fold:</strong> {foldName}
              </p>
            </div>
          )}

          {loadingPayloads && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading payloads…
            </div>
          )}

          {payloads.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roleplay Payload <span className="text-red-500">*</span>
              </label>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
                {payloads.map((payload) => (
                  <label key={payload.id} className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${selectedPayloadId === payload.id ? 'bg-primary/5' : ''}`}>
                    <input
                      type="radio"
                      name="payload"
                      value={payload.id}
                      checked={selectedPayloadId === payload.id}
                      onChange={() => {
                        setSelectedPayloadId(payload.id)
                        setSelectedPayloadName(payload.name)
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{payload.name}</span>
                        <span className="text-xs text-gray-500">ID: {payload.id}</span>
                      </div>
                      {payload.description && <p className="text-sm text-gray-500 truncate">{payload.description}</p>}
                    </div>
                  </label>
                ))}
              </div>
              {selectedPayloadId && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPayloadId('')
                    setSelectedPayloadName('')
                  }}
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </button>
              )}
            </div>
          )}

          {/* Avatar Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Avatar</label>
            <div className="relative" ref={avatarDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setAvatarDropdownOpen((v) => !v)
                  if (!avatarDropdownOpen) fetchAvatars('')
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent flex items-center justify-between text-left"
              >
                <span className="flex items-center gap-2 text-sm">
                  {avatarId ? (
                    <>
                      <img
                        src={`${API_URL}/api/avatars/${avatarId}/image`}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                      <span className="text-gray-900">{selectedAvatarName}</span>
                    </>
                  ) : (
                    <>
                      <UserCircle className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-400">No avatar (uses default)</span>
                    </>
                  )}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
              </button>

              {avatarDropdownOpen && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={avatarSearch}
                      onChange={(e) => setAvatarSearch(e.target.value)}
                      placeholder="Search avatars..."
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {loadingAvatars ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleAvatarSelect(null)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${!avatarId ? 'bg-primary/5 text-primary' : 'text-gray-500'}`}
                        >
                          <UserCircle className="w-5 h-5" />
                          No avatar (uses default)
                        </button>
                        {avatarOptions.map((av) => (
                          <button
                            key={av.id}
                            type="button"
                            onClick={() => handleAvatarSelect(av)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${avatarId === av.id ? 'bg-primary/5' : ''}`}
                          >
                            <img
                              src={`${API_URL}/api/avatars/${av.id}/image`}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover shrink-0"
                              onError={(e) => {
                                ;(e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                            <span className="font-medium text-gray-900 truncate">{av.name}</span>
                            {av.description && <span className="text-gray-400 text-xs truncate">{av.description}</span>}
                          </button>
                        ))}
                        {avatarOptions.length === 0 && !loadingAvatars && <p className="text-sm text-gray-400 text-center py-3">No avatars found</p>}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Avatar provides the ElevenLabs agent for voice roleplay</p>
          </div>

          {/* Coach Payload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coach Payload</label>
            {loadingCoachPayloads ? (
              <div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading payloads…
              </div>
            ) : coachPayloadsError ? (
              <div className="flex items-center gap-2 px-4 py-2 border border-red-200 bg-red-50 rounded-lg text-sm text-red-700">
                <span className="flex-1">{coachPayloadsError}</span>
                <button type="button" onClick={() => selectedModule?.microfankProjectId && fetchFoldsAndPayloads(selectedModule.microfankProjectId)} className="shrink-0">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <select
                value={selectedCoachPayloadId ?? ''}
                onChange={(e) => setSelectedCoachPayloadId(e.target.value ? parseInt(e.target.value, 10) : null)}
                disabled={!hasProject}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">None</option>
                {coachPayloads.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                {coachPayloadFallback && !coachPayloads.find((p) => p.id === coachPayloadFallback.id) && <option value={coachPayloadFallback.id}>{coachPayloadFallback.name}</option>}
              </select>
            )}
            <p className="text-xs text-gray-400 mt-0.5">Used for voice/video mode instructions and the context tooltip</p>
          </div>

          {/* Min Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Rating</label>
            <input
              type="number"
              value={minRating}
              onChange={(e) => setMinRating(e.target.value)}
              min="0"
              max="100"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Min Sessions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Sessions</label>
            <input
              type="number"
              value={minSessions}
              onChange={(e) => setMinSessions(e.target.value)}
              min="0"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !selectedModuleId || (!selectedPayloadId && !selectedPayloadName)}>
              {submitting ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save Changes' : 'Create Target'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateTargetModal
